import { DataService } from '../services/data.js';
import { formatMoney, getIcon, showToast } from '../components/ui.js';

let selectedClientId = 'all';
let filterDateFrom = '';
let filterDateTo = '';
let projectSearchTerm = '';
let paymentSearchTerm = '';
let projectsCollapsed = false;
let paymentsCollapsed = false;
let projectsSortField = 'name';
let projectsSortAsc = true;
let paymentsSortField = 'payment_date';
let paymentsSortAsc = false;

export async function renderFinanceView() {
  const [clients, projects, finSummary] = await Promise.all([
    DataService.getClients(),
    DataService.getProjects(),
    DataService.getFinancialSummary({
      clientId: selectedClientId,
      dateFrom: filterDateFrom,
      dateTo: filterDateTo
    })
  ]);

  const projectSummaries = finSummary.projectSummaries || [];
  const payments = finSummary.filteredPayments || [];

  // Filter projects in table
  const pTerm = projectSearchTerm.toLowerCase().trim();
  const filteredProjectSummaries = projectSummaries.filter(p => {
    if (p.isInternal) return false;
    if (!pTerm) return true;
    return (p.name || '').toLowerCase().includes(pTerm) ||
      (p.client?.name || '').toLowerCase().includes(pTerm) ||
      (p.billing_mode || '').toLowerCase().includes(pTerm) ||
      (p.revenue_type || '').toLowerCase().includes(pTerm) ||
      String(p.devengado || '').includes(pTerm) ||
      String(p.cobrado || '').includes(pTerm) ||
      String(p.facturado || '').includes(pTerm);
  });

  // Sort projects in table
  const sortedProjectSummaries = [...filteredProjectSummaries].sort((a, b) => {
    let cmp = 0;
    if (projectsSortField === 'name') {
      cmp = (a.name || '').localeCompare(b.name || '');
    } else if (projectsSortField === 'client') {
      cmp = (a.client?.name || '').localeCompare(b.client?.name || '');
    } else if (projectsSortField === 'billing_mode') {
      const mA = a.revenue_type === 'recurrente' ? 'Recurrente' : a.billing_mode === 'time_and_materials' ? 'Horas Reales' : 'Presupuesto';
      const mB = b.revenue_type === 'recurrente' ? 'Recurrente' : b.billing_mode === 'time_and_materials' ? 'Horas Reales' : 'Presupuesto';
      cmp = mA.localeCompare(mB);
    } else if (projectsSortField === 'devengado') {
      cmp = (a.devengado || 0) - (b.devengado || 0);
    } else if (projectsSortField === 'cobrado') {
      cmp = (a.cobrado || 0) - (b.cobrado || 0);
    } else if (projectsSortField === 'facturado') {
      cmp = (a.facturado || 0) - (b.facturado || 0);
    } else if (projectsSortField === 'pendienteCobro') {
      cmp = (a.pendienteCobro || 0) - (b.pendienteCobro || 0);
    }
    return projectsSortAsc ? cmp : -cmp;
  });

  // Calculate projects table totals
  const projectTotals = filteredProjectSummaries.reduce((acc, p) => {
    acc.devengado += Number(p.devengado || 0);
    acc.cobrado += Number(p.cobrado || 0);
    acc.facturado += Number(p.facturado || 0);
    acc.pendienteCobro += Number(p.pendienteCobro || 0);
    return acc;
  }, { devengado: 0, cobrado: 0, facturado: 0, pendienteCobro: 0 });

  projectTotals.devengado = Math.round(projectTotals.devengado * 100) / 100;
  projectTotals.cobrado = Math.round(projectTotals.cobrado * 100) / 100;
  projectTotals.facturado = Math.round(projectTotals.facturado * 100) / 100;
  projectTotals.pendienteCobro = Math.round(projectTotals.pendienteCobro * 100) / 100;

  // Filter payments
  const payTerm = paymentSearchTerm.toLowerCase().trim();
  const filteredPayments = payments.filter(pay => {
    if (!payTerm) return true;
    return (pay.project?.name || '').toLowerCase().includes(payTerm) ||
      (pay.client?.name || '').toLowerCase().includes(payTerm) ||
      (pay.invoice_file || '').toLowerCase().includes(payTerm) ||
      (pay.receipt_file || '').toLowerCase().includes(payTerm) ||
      (pay.notes || '').toLowerCase().includes(payTerm) ||
      (pay.payment_date || '').includes(payTerm) ||
      (pay.imputation_date || '').includes(payTerm) ||
      String(pay.amount_paid || '').includes(payTerm);
  });

  // Sort payments
  const sortedPayments = [...filteredPayments].sort((a, b) => {
    let cmp = 0;
    if (paymentsSortField === 'payment_date') {
      cmp = (a.payment_date || '').localeCompare(b.payment_date || '');
    } else if (paymentsSortField === 'imputation_date') {
      const dA = a.imputation_date || a.payment_date || '';
      const dB = b.imputation_date || b.payment_date || '';
      cmp = dA.localeCompare(dB);
    } else if (paymentsSortField === 'project') {
      cmp = (a.project?.name || '').localeCompare(b.project?.name || '');
    } else if (paymentsSortField === 'client') {
      cmp = (a.client?.name || '').localeCompare(b.client?.name || '');
    } else if (paymentsSortField === 'amount_paid') {
      cmp = (Number(a.amount_paid) || 0) - (Number(b.amount_paid) || 0);
    } else if (paymentsSortField === 'invoice_file') {
      const fA = a.invoice_file || a.receipt_file || '';
      const fB = b.invoice_file || b.receipt_file || '';
      cmp = fA.localeCompare(fB);
    } else if (paymentsSortField === 'notes') {
      cmp = (a.notes || '').localeCompare(b.notes || '');
    }
    return paymentsSortAsc ? cmp : -cmp;
  });

  // Calculate payments table total
  const totalCobradoPayments = Math.round(filteredPayments.reduce((acc, pay) => {
    return acc + Number(pay.amount_paid || 0);
  }, 0) * 100) / 100;

  // Helper for sortable table headers
  const renderSortableTh = (table, field, label, currentField, currentAsc, align = 'left') => {
    const isCurrent = currentField === field;
    const icon = isCurrent
      ? (currentAsc ? getIcon('chevronUp', 'w-3.5 h-3.5 text-primary shrink-0') : getIcon('chevronDown', 'w-3.5 h-3.5 text-primary shrink-0'))
      : getIcon('arrowUpDown', 'w-3 h-3 text-text-secondary/40 group-hover:text-text-secondary shrink-0 transition-colors');

    return `
      <th 
        class="px-4 py-3 cursor-pointer select-none group hover:bg-surface-subtle transition-colors ${align === 'right' ? 'text-right' : 'text-left'}"
        data-sort-table="${table}"
        data-sort-field="${field}"
        title="Ordenar por ${label}"
      >
        <div class="flex items-center gap-1.5 ${align === 'right' ? 'justify-end' : 'justify-start'}">
          <span class="${isCurrent ? 'text-primary font-bold' : ''}">${label}</span>
          ${icon}
        </div>
      </th>
    `;
  };

  const hasActiveFilters = selectedClientId !== 'all' || Boolean(filterDateFrom) || Boolean(filterDateTo);

  return `
    <div class="space-y-6">
      <!-- Header -->
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-3 border-b border-border">
        <div>
          <h1 class="text-2xl font-bold tracking-tight text-text-primary">Finanzas, Facturación y Cobros</h1>
          <p class="text-xs text-text-secondary mt-0.5">Control de ingresos devengados, cobranzas reales, facturación fiscal y saldos pendientes</p>
        </div>
        <button id="btn-open-payment-modal" class="px-3.5 py-2 text-xs font-semibold rounded-md bg-primary text-white hover:bg-primary-hover active:bg-primary-active transition-all shadow-sm flex items-center gap-2">
          ${getIcon('plus', 'w-3.5 h-3.5')}
          <span>+ Registrar Cobro</span>
        </button>
      </div>

      <!-- Filtros: Cliente y Rango de Fechas -->
      <div class="bg-surface p-4 rounded-md border border-border shadow-card flex flex-wrap items-center justify-between gap-4">
        <div class="flex flex-wrap items-center gap-4">
          <!-- Filtro Cliente -->
          <div class="flex items-center gap-2">
            <label for="filter-finance-client" class="text-xs font-semibold text-text-secondary flex items-center gap-1">
              ${getIcon('users', 'w-3.5 h-3.5 text-text-secondary')}
              Cliente:
            </label>
            <select id="filter-finance-client" class="text-xs border border-border rounded-md px-2.5 py-1.5 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none">
              <option value="all" ${selectedClientId === 'all' ? 'selected' : ''}>Todos los clientes</option>
              ${clients.filter(c => c.id !== 12).map(c => `
                <option value="${c.id}" ${String(selectedClientId) === String(c.id) ? 'selected' : ''}>${c.name}</option>
              `).join('')}
            </select>
          </div>

          <!-- Filtro Rango de Fechas -->
          <div class="flex items-center gap-2">
            <span class="text-xs font-semibold text-text-secondary flex items-center gap-1">
              ${getIcon('calendar', 'w-3.5 h-3.5 text-text-secondary')}
              Período:
            </span>
            <div class="flex items-center gap-1.5">
              <label for="filter-finance-date-from" class="text-[11px] text-text-secondary">Desde</label>
              <input 
                type="date" 
                id="filter-finance-date-from" 
                value="${filterDateFrom}" 
                class="text-xs font-mono border border-border rounded-md px-2 py-1 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none" 
              />
            </div>
            <div class="flex items-center gap-1.5">
              <label for="filter-finance-date-to" class="text-[11px] text-text-secondary">Hasta</label>
              <input 
                type="date" 
                id="filter-finance-date-to" 
                value="${filterDateTo}" 
                class="text-xs font-mono border border-border rounded-md px-2 py-1 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none" 
              />
            </div>
          </div>
        </div>

        <!-- Presets rápidos y Limpiar Filtros -->
        <div class="flex items-center gap-1.5 flex-wrap">
          <button type="button" class="btn-finance-preset px-2.5 py-1 text-[11px] font-medium rounded border border-border bg-surface hover:bg-surface-subtle text-text-secondary hover:text-text-primary transition-colors ${!filterDateFrom && !filterDateTo ? 'font-bold bg-surface-subtle text-text-primary' : ''}" data-preset="all">Histórico</button>
          <button type="button" class="btn-finance-preset px-2.5 py-1 text-[11px] font-medium rounded border border-border bg-surface hover:bg-surface-subtle text-text-secondary hover:text-text-primary transition-colors" data-preset="this-month">Este mes</button>
          <button type="button" class="btn-finance-preset px-2.5 py-1 text-[11px] font-medium rounded border border-border bg-surface hover:bg-surface-subtle text-text-secondary hover:text-text-primary transition-colors" data-preset="last-month">Mes anterior</button>
          <button type="button" class="btn-finance-preset px-2.5 py-1 text-[11px] font-medium rounded border border-border bg-surface hover:bg-surface-subtle text-text-secondary hover:text-text-primary transition-colors" data-preset="this-year">Este año</button>
          ${hasActiveFilters ? `
            <button type="button" id="btn-clear-finance-filters" class="px-2.5 py-1 text-[11px] font-semibold rounded text-red-600 hover:bg-red-50 border border-red-200 transition-colors ml-1">
              Limpiar filtros
            </button>
          ` : ''}
        </div>
      </div>

      <!-- Financial Metric Cards (4 KPIs Solicitados: Importe devengado | Importe cobrado | Importe Facturado | Devengado pendiente de cobro) -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <!-- Card 1: Importe devengado -->
        <div class="bg-surface p-5 rounded-md border border-border shadow-card">
          <span class="text-xs font-semibold text-text-secondary uppercase tracking-wider">Importe Devengado</span>
          <p class="text-2xl font-bold font-mono text-text-primary mt-2">${formatMoney(finSummary.totalDevengado)}</p>
          <p class="text-[11px] text-text-secondary font-mono mt-1">Horas reales + abonos + presupuestos</p>
        </div>

        <!-- Card 2: Importe cobrado -->
        <div class="bg-surface p-5 rounded-md border border-border shadow-card">
          <span class="text-xs font-semibold text-text-secondary uppercase tracking-wider">Importe Cobrado</span>
          <p class="text-2xl font-bold font-mono text-emerald-600 mt-2">${formatMoney(finSummary.totalCobrado)}</p>
          <span class="text-[11px] text-emerald-700 font-mono mt-1 block">${finSummary.paymentsCount} cobranzas registradas</span>
        </div>

        <!-- Card 3: Importe Facturado -->
        <div class="bg-surface p-5 rounded-md border border-border shadow-card">
          <span class="text-xs font-semibold text-text-secondary uppercase tracking-wider">Importe Facturado</span>
          <p class="text-2xl font-bold font-mono text-text-primary mt-2">${formatMoney(finSummary.totalFacturado)}</p>
          <span class="text-[11px] text-text-secondary font-mono mt-1 block">${finSummary.invoicesCount} facturas fiscales emitidas</span>
        </div>

        <!-- Card 4: Devengado pendiente de cobro -->
        <div class="bg-surface p-5 rounded-md border border-border shadow-card">
          <span class="text-xs font-semibold text-text-secondary uppercase tracking-wider">Devengado Pendiente de Cobro</span>
          <p class="text-2xl font-bold font-mono text-amber-700 mt-2">${formatMoney(finSummary.devengadoPendienteCobro)}</p>
          <span class="text-[11px] text-text-secondary font-mono mt-1 block" title="Suma de saldos pendientes por cobrar de cada proyecto">Suma de saldos pendientes por proyecto</span>
        </div>
      </div>

      <!-- Tables: Devengamiento y Cobranzas por Proyecto + Historial de Pagos -->
      <div class="space-y-6">
        <!-- Tabla: Facturación por Proyecto -->
        <div class="bg-surface rounded-md border border-border shadow-card overflow-hidden">
          <div class="p-4 border-b border-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-surface-subtle/50">
            <div class="flex items-center gap-3">
              <h3 class="font-bold text-sm text-text-primary">Facturación por Proyecto</h3>
              <span class="text-xs font-mono px-2 py-0.5 rounded bg-surface border border-border text-text-secondary">
                ${filteredProjectSummaries.length === projectSummaries.filter(p => !p.isInternal).length 
                  ? `${filteredProjectSummaries.length} proyectos` 
                  : `${filteredProjectSummaries.length} de ${projectSummaries.filter(p => !p.isInternal).length}`}
              </span>
            </div>

            <!-- Controls: Search & Collapse -->
            <div class="flex items-center gap-2">
              <div class="relative min-w-[200px] sm:min-w-[240px]">
                <span class="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none text-text-secondary">
                  ${getIcon('search', 'w-3 h-3')}
                </span>
                <input 
                  type="text" 
                  id="input-search-projects-fin" 
                  placeholder="Buscar proyecto, cliente, monto..." 
                  value="${projectSearchTerm}" 
                  class="w-full pl-7 pr-7 py-1 text-xs bg-surface border border-border rounded-md text-text-primary placeholder:text-text-secondary/60 focus:ring-2 focus:ring-primary outline-none transition-all"
                />
                ${projectSearchTerm ? `
                  <button id="btn-clear-projects-fin-search" class="absolute inset-y-0 right-0 pr-2 flex items-center text-text-secondary hover:text-text-primary text-xs" title="Limpiar búsqueda">✕</button>
                ` : ''}
              </div>

              <button 
                id="btn-toggle-projects-collapse" 
                class="flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold border border-border rounded-md bg-surface hover:bg-surface-subtle text-text-secondary transition-colors"
                title="${projectsCollapsed ? 'Expandir tabla' : 'Plegar tabla'}"
              >
                <span class="collapse-icon">${getIcon(projectsCollapsed ? 'chevron-right' : 'chevron-down', 'w-3.5 h-3.5')}</span>
                <span class="collapse-label">${projectsCollapsed ? 'Expandir' : 'Plegar'}</span>
              </button>
            </div>
          </div>

          <div id="container-projects-fin-table" class="overflow-x-auto ${projectsCollapsed ? 'hidden' : ''}">
            <table class="w-full text-left text-xs">
              <thead class="bg-surface-subtle border-b border-border text-text-secondary uppercase font-semibold">
                <tr>
                  ${renderSortableTh('projects', 'name', 'Proyecto', projectsSortField, projectsSortAsc)}
                  ${renderSortableTh('projects', 'client', 'Cliente', projectsSortField, projectsSortAsc)}
                  ${renderSortableTh('projects', 'billing_mode', 'Modalidad', projectsSortField, projectsSortAsc)}
                  ${renderSortableTh('projects', 'devengado', 'Importe Devengado', projectsSortField, projectsSortAsc, 'right')}
                  ${renderSortableTh('projects', 'cobrado', 'Importe Cobrado', projectsSortField, projectsSortAsc, 'right')}
                  ${renderSortableTh('projects', 'facturado', 'Importe Facturado', projectsSortField, projectsSortAsc, 'right')}
                  ${renderSortableTh('projects', 'pendienteCobro', 'Devengado Pendiente', projectsSortField, projectsSortAsc, 'right')}
                </tr>
              </thead>
              <tbody class="divide-y divide-border/60 font-medium">
                ${sortedProjectSummaries.length === 0 ? `
                  <tr>
                    <td colspan="7" class="px-4 py-8 text-center text-text-secondary">
                      ${projectSearchTerm ? `No se encontraron proyectos que coincidan con "${projectSearchTerm}"` : 'Sin proyectos en el período seleccionado'}
                    </td>
                  </tr>
                ` : sortedProjectSummaries.map(p => {
                  const isRecurring = p.revenue_type === 'recurrente';
                  const isTm = p.billing_mode === 'time_and_materials';

                  return `
                    <tr class="hover:bg-surface-subtle/50 transition-colors">
                      <td class="px-4 py-3 font-bold text-text-primary">${p.name}</td>
                      <td class="px-4 py-3 text-text-secondary font-mono">${p.client?.name || '-'}</td>
                      <td class="px-4 py-3">
                        <span class="px-1.5 py-0.5 rounded font-mono text-[10px] ${
                          isRecurring 
                            ? 'bg-purple-50 text-purple-700 border border-purple-200' 
                            : isTm 
                              ? 'bg-blue-50 text-blue-700 border border-blue-200' 
                              : 'bg-zinc-100 text-zinc-700 border border-zinc-200'
                        }">
                          ${isRecurring ? 'Recurrente' : isTm ? 'Horas Reales' : 'Presupuesto'}
                        </span>
                      </td>
                      <td class="px-4 py-3 font-mono font-bold text-text-primary text-right whitespace-nowrap">${formatMoney(p.devengado, p.currency)}</td>
                      <td class="px-4 py-3 font-mono text-emerald-600 font-bold text-right whitespace-nowrap">${formatMoney(p.cobrado, p.currency)}</td>
                      <td class="px-4 py-3 font-mono font-medium text-text-primary text-right whitespace-nowrap">${formatMoney(p.facturado, p.currency)}</td>
                      <td class="px-4 py-3 font-mono text-right whitespace-nowrap">
                        ${p.pendienteCobro > 0 
                          ? `<span class="font-bold text-amber-700">${formatMoney(p.pendienteCobro, p.currency)}</span>` 
                          : `<span class="px-1.5 py-0.5 rounded text-[10px] font-mono bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold">Al día ($0)</span>`}
                      </td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
              <tfoot class="bg-surface-subtle/80 font-bold border-t-2 border-border text-xs text-text-primary">
                <tr>
                  <td class="px-4 py-3" colspan="3">
                    <div class="flex items-center gap-1.5">
                      <span class="uppercase tracking-wider text-[11px] text-text-secondary font-bold">Totales</span>
                      <span class="font-normal text-text-secondary text-[11px]">(${filteredProjectSummaries.length} ${filteredProjectSummaries.length === 1 ? 'proyecto' : 'proyectos'}${projectSearchTerm ? ' filtrados' : ''})</span>
                    </div>
                  </td>
                  <td class="px-4 py-3 font-mono font-bold text-text-primary text-right whitespace-nowrap">${formatMoney(projectTotals.devengado)}</td>
                  <td class="px-4 py-3 font-mono font-bold text-emerald-600 text-right whitespace-nowrap">${formatMoney(projectTotals.cobrado)}</td>
                  <td class="px-4 py-3 font-mono font-bold text-text-primary text-right whitespace-nowrap">${formatMoney(projectTotals.facturado)}</td>
                  <td class="px-4 py-3 font-mono text-right whitespace-nowrap">
                    ${projectTotals.pendienteCobro > 0 
                      ? `<span class="font-bold text-amber-700">${formatMoney(projectTotals.pendienteCobro)}</span>` 
                      : `<span class="px-1.5 py-0.5 rounded text-[10px] font-mono bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold">Al día ($0)</span>`}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <!-- Registro de Cobros y Pagos -->
        <div class="bg-surface rounded-md border border-border shadow-card overflow-hidden">
          <div class="p-4 border-b border-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-surface-subtle/50">
            <div class="flex items-center gap-3">
              <h3 class="font-bold text-sm text-text-primary">Historial de Cobros y Transferencias</h3>
              <span class="text-xs font-mono px-2 py-0.5 rounded bg-surface border border-border text-text-secondary">
                ${filteredPayments.length === payments.length ? `${payments.length} cobros` : `${filteredPayments.length} de ${payments.length}`}
              </span>
            </div>

            <!-- Controls: Search & Collapse -->
            <div class="flex items-center gap-2">
              <div class="relative min-w-[200px] sm:min-w-[240px]">
                <span class="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none text-text-secondary">
                  ${getIcon('search', 'w-3 h-3')}
                </span>
                <input 
                  type="text" 
                  id="input-search-payments" 
                  placeholder="Buscar cobro, cliente, comprobante, nota..." 
                  value="${paymentSearchTerm}" 
                  class="w-full pl-7 pr-7 py-1 text-xs bg-surface border border-border rounded-md text-text-primary placeholder:text-text-secondary/60 focus:ring-2 focus:ring-primary outline-none transition-all"
                />
                ${paymentSearchTerm ? `
                  <button id="btn-clear-payments-search" class="absolute inset-y-0 right-0 pr-2 flex items-center text-text-secondary hover:text-text-primary text-xs" title="Limpiar búsqueda">✕</button>
                ` : ''}
              </div>

              <button 
                id="btn-toggle-payments-collapse" 
                class="flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold border border-border rounded-md bg-surface hover:bg-surface-subtle text-text-secondary transition-colors"
                title="${paymentsCollapsed ? 'Expandir tabla' : 'Plegar tabla'}"
              >
                <span class="collapse-icon">${getIcon(paymentsCollapsed ? 'chevron-right' : 'chevron-down', 'w-3.5 h-3.5')}</span>
                <span class="collapse-label">${paymentsCollapsed ? 'Expandir' : 'Plegar'}</span>
              </button>
            </div>
          </div>

          <div id="container-payments-table" class="overflow-x-auto ${paymentsCollapsed ? 'hidden' : ''}">
            <table class="w-full text-left text-xs">
              <thead class="bg-surface-subtle border-b border-border text-text-secondary uppercase font-semibold">
                <tr>
                  ${renderSortableTh('payments', 'payment_date', 'Fecha de Cobro', paymentsSortField, paymentsSortAsc)}
                  ${renderSortableTh('payments', 'imputation_date', 'Fecha Imputación', paymentsSortField, paymentsSortAsc)}
                  ${renderSortableTh('payments', 'project', 'Proyecto', paymentsSortField, paymentsSortAsc)}
                  ${renderSortableTh('payments', 'client', 'Cliente', paymentsSortField, paymentsSortAsc)}
                  ${renderSortableTh('payments', 'amount_paid', 'Importe Cobrado', paymentsSortField, paymentsSortAsc, 'right')}
                  ${renderSortableTh('payments', 'notes', 'Notas', paymentsSortField, paymentsSortAsc)}
                  <th class="px-4 py-3 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-border/60 font-medium">
                ${sortedPayments.length === 0 ? `
                  <tr>
                    <td colspan="7" class="px-4 py-8 text-center text-text-secondary">
                      ${paymentSearchTerm ? `No se encontraron cobros que coincidan con "${paymentSearchTerm}"` : 'Sin cobros registrados en el período'}
                    </td>
                  </tr>
                ` : sortedPayments.map(pay => `
                  <tr class="hover:bg-surface-subtle/50 transition-colors">
                    <td class="px-4 py-3 font-mono text-text-secondary whitespace-nowrap">${pay.payment_date}</td>
                    <td class="px-4 py-3 font-mono whitespace-nowrap">
                      ${pay.imputation_date 
                        ? `<span class="px-1.5 py-0.5 rounded text-[10px] bg-blue-50 text-blue-700 border border-blue-200 font-semibold">${pay.imputation_date}</span>` 
                        : `<span class="text-text-secondary text-[11px]" title="Sin imputación específica (toma fecha de cobro)">${pay.payment_date}</span>`}
                    </td>
                    <td class="px-4 py-3 font-bold text-text-primary">${pay.project?.name || '-'}</td>
                    <td class="px-4 py-3 text-text-secondary">${pay.client?.name || '-'}</td>
                    <td class="px-4 py-3 font-mono font-bold text-emerald-600 text-right whitespace-nowrap">${formatMoney(pay.amount_paid)}</td>
                    <td class="px-4 py-3 text-text-secondary text-[11px] whitespace-nowrap">
                      ${pay.notes && pay.notes.trim() ? `
                        <div class="group/note relative inline-flex items-center">
                          <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-surface-subtle hover:bg-surface border border-border hover:border-zinc-300 text-text-secondary hover:text-text-primary cursor-pointer transition-colors shadow-2xs" title="${pay.notes.replace(/"/g, '&quot;')}">
                            ${getIcon('messageSquare', 'w-3 h-3 text-primary shrink-0')}
                            <span>Nota</span>
                          </span>
                          <div class="pointer-events-none absolute left-0 bottom-full mb-1.5 hidden group-hover/note:block z-30 w-max max-w-xs rounded bg-zinc-900 px-2.5 py-1.5 text-[11px] text-white shadow-xl whitespace-normal break-words leading-tight animate-fade-in">
                            ${pay.notes.replace(/</g, '&lt;').replace(/>/g, '&gt;')}
                          </div>
                        </div>
                      ` : `
                        <span class="text-text-secondary/40 text-[11px]">-</span>
                      `}
                    </td>
                    <td class="px-4 py-3 text-right whitespace-nowrap">
                      <div class="flex items-center justify-end gap-1.5">
                        <button 
                          class="btn-edit-payment p-1 text-text-secondary hover:text-text-primary rounded hover:bg-surface-subtle transition-colors" 
                          data-payment='${JSON.stringify({
                            id: pay.id,
                            project_id: pay.project_id,
                            client_id: pay.client_id,
                            payment_date: pay.payment_date,
                            imputation_date: pay.imputation_date || '',
                            amount_paid: pay.amount_paid,
                            invoice_file: pay.invoice_file || '',
                            notes: pay.notes || ''
                          }).replace(/'/g, '&apos;')}'
                          title="Editar cobro"
                        >
                          ${getIcon('edit', 'w-3.5 h-3.5')}
                        </button>
                        <button 
                          class="btn-delete-payment p-1 text-text-secondary hover:text-red-600 rounded hover:bg-surface-subtle transition-colors" 
                          data-payment-id="${pay.id}"
                          data-payment-amount="${pay.amount_paid}"
                          title="Eliminar cobro"
                        >
                          ${getIcon('trash', 'w-3.5 h-3.5')}
                        </button>
                      </div>
                    </td>
                  </tr>
                `).join('')}
              </tbody>
              <tfoot class="bg-surface-subtle/80 font-bold border-t-2 border-border text-xs text-text-primary">
                <tr>
                  <td class="px-4 py-3" colspan="4">
                    <div class="flex items-center gap-1.5">
                      <span class="uppercase tracking-wider text-[11px] text-text-secondary font-bold">Total Cobrado</span>
                      <span class="font-normal text-text-secondary text-[11px]">(${filteredPayments.length} ${filteredPayments.length === 1 ? 'cobro' : 'cobros'}${paymentSearchTerm ? ' filtrados' : ''})</span>
                    </div>
                  </td>
                  <td class="px-4 py-3 font-mono font-bold text-emerald-600 text-right whitespace-nowrap">${formatMoney(totalCobradoPayments)}</td>
                  <td class="px-4 py-3" colspan="2"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Registrar Cobro -->
    <div id="modal-payment" class="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 hidden flex items-center justify-center p-4">
      <div class="bg-surface rounded-md border border-border shadow-xl max-w-md w-full p-6 space-y-4 animate-fade-in">
        <div class="flex items-center justify-between pb-3 border-b border-border">
          <h3 class="font-bold text-base text-text-primary">Registrar Nuevo Cobro</h3>
          <button id="btn-close-payment-modal" class="text-text-secondary hover:text-text-primary text-sm font-bold">✕</button>
        </div>

        <form id="form-payment" class="space-y-3.5">
          <div>
            <label class="block text-xs font-semibold text-text-primary mb-1">Proyecto</label>
            <select id="modal-pay-project" class="w-full text-xs border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none">
              ${projects.map(p => `<option value="${p.id}">${p.name} (${p.client?.name || 'Cliente'})</option>`).join('')}
            </select>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 items-start">
            <div>
              <label class="block text-xs font-semibold text-text-primary mb-1 h-5 flex items-center">
                <span>Fecha de Cobro <span class="text-[10px] text-text-secondary font-normal">(Efectiva)</span></span>
              </label>
              <input type="date" id="modal-pay-date" value="${new Date().toISOString().split('T')[0]}" class="w-full text-xs font-mono border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none" required />
            </div>
            <div>
              <label class="block text-xs font-semibold text-text-primary mb-1 h-5 flex items-center">
                <span>Fecha Imputación <span class="text-[10px] text-text-secondary font-normal">(Devengado)</span></span>
              </label>
              <input type="date" id="modal-pay-imputation-date" value="${new Date().toISOString().split('T')[0]}" class="w-full text-xs font-mono border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none" title="Mes o fecha a la que corresponde este cobro" />
            </div>
          </div>

          <div>
            <label class="block text-xs font-semibold text-text-primary mb-1">Importe Cobrado ($)</label>
            <input type="number" step="0.01" id="modal-pay-amount" placeholder="50000" class="w-full text-xs font-mono font-bold border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none" required />
          </div>

          <div>
            <label class="block text-xs font-semibold text-text-primary mb-1">Número de Factura o Archivo</label>
            <input type="text" id="modal-pay-invoice-file" placeholder="Ej: Factura_B_0001.pdf" class="w-full text-xs font-mono border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none" />
          </div>

          <div>
            <label class="block text-xs font-semibold text-text-primary mb-1">Observaciones / Comprobante</label>
            <textarea id="modal-pay-notes" rows="2" placeholder="Detalle sobre medio de cobro (Payoneer, Takenos, Banco)..." class="w-full text-xs border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none"></textarea>
          </div>

          <div class="pt-3 border-t border-border flex justify-end gap-2">
            <button type="button" id="btn-cancel-payment" class="px-4 py-2 text-xs font-semibold rounded-md border border-border hover:bg-surface-subtle text-text-secondary">
              Cancelar
            </button>
            <button type="submit" class="px-4 py-2 text-xs font-semibold rounded-md bg-primary text-white hover:bg-primary-hover active:bg-primary-active">
              Guardar Cobro
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Modal Editar Cobro -->
    <div id="modal-edit-payment" class="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 hidden flex items-center justify-center p-4">
      <div class="bg-surface rounded-md border border-border shadow-xl max-w-md w-full p-6 space-y-4 animate-fade-in">
        <div class="flex items-center justify-between pb-3 border-b border-border">
          <h3 class="font-bold text-base text-text-primary">Editar Cobro</h3>
          <button id="btn-close-edit-payment-modal" class="text-text-secondary hover:text-text-primary text-sm font-bold">✕</button>
        </div>

        <form id="form-edit-payment" class="space-y-3.5">
          <input type="hidden" id="edit-pay-id" />

          <div>
            <label class="block text-xs font-semibold text-text-primary mb-1">Proyecto</label>
            <select id="edit-pay-project" class="w-full text-xs border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none" required>
              ${projects.map(p => `<option value="${p.id}">${p.name} (${p.client?.name || 'Cliente'})</option>`).join('')}
            </select>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 items-start">
            <div>
              <label class="block text-xs font-semibold text-text-primary mb-1 h-5 flex items-center">
                <span>Fecha de Cobro <span class="text-[10px] text-text-secondary font-normal">(Efectiva)</span></span>
              </label>
              <input type="date" id="edit-pay-date" class="w-full text-xs font-mono border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none" required />
            </div>
            <div>
              <label class="block text-xs font-semibold text-text-primary mb-1 h-5 flex items-center">
                <span>Fecha Imputación <span class="text-[10px] text-text-secondary font-normal">(Devengado)</span></span>
              </label>
              <input type="date" id="edit-pay-imputation-date" class="w-full text-xs font-mono border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none" title="Mes o fecha a la que corresponde este cobro" />
            </div>
          </div>

          <div>
            <label class="block text-xs font-semibold text-text-primary mb-1">Importe Cobrado ($)</label>
            <input type="number" step="0.01" id="edit-pay-amount" class="w-full text-xs font-mono font-bold border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none" required />
          </div>

          <div>
            <label class="block text-xs font-semibold text-text-primary mb-1">Número de Factura o Archivo</label>
            <input type="text" id="edit-pay-invoice-file" placeholder="Ej: Factura_B_0001.pdf" class="w-full text-xs font-mono border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none" />
          </div>

          <div>
            <label class="block text-xs font-semibold text-text-primary mb-1">Observaciones / Comprobante</label>
            <textarea id="edit-pay-notes" rows="2" placeholder="Detalle sobre medio de cobro (Payoneer, Takenos, Banco)..." class="w-full text-xs border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none"></textarea>
          </div>

          <div class="pt-3 border-t border-border flex justify-end gap-2">
            <button type="button" id="btn-cancel-edit-payment" class="px-4 py-2 text-xs font-semibold rounded-md border border-border hover:bg-surface-subtle text-text-secondary">
              Cancelar
            </button>
            <button type="submit" class="px-4 py-2 text-xs font-semibold rounded-md bg-primary text-white hover:bg-primary-hover active:bg-primary-active">
              Actualizar Cobro
            </button>
          </div>
        </form>
      </div>
    </div>
  `;
}

export function initFinanceEvents() {
  const modal = document.getElementById('modal-payment');
  const btnOpen = document.getElementById('btn-open-payment-modal');
  const btnClose = document.getElementById('btn-close-payment-modal');
  const btnCancel = document.getElementById('btn-cancel-payment');

  if (btnOpen) {
    btnOpen.onclick = () => {
      modal.classList.remove('hidden');
      document.body.classList.add('overflow-hidden');
    };
  }
  [btnClose, btnCancel].forEach(b => {
    if (b) {
      b.onclick = () => {
        modal.classList.add('hidden');
        document.body.classList.remove('overflow-hidden');
      };
    }
  });

  const modalEdit = document.getElementById('modal-edit-payment');
  const btnCloseEdit = document.getElementById('btn-close-edit-payment-modal');
  const btnCancelEdit = document.getElementById('btn-cancel-edit-payment');

  const closeEditModal = () => {
    if (modalEdit) {
      modalEdit.classList.add('hidden');
      document.body.classList.remove('overflow-hidden');
    }
  };

  [btnCloseEdit, btnCancelEdit].forEach(b => {
    if (b) b.onclick = closeEditModal;
  });

  const form = document.getElementById('form-payment');
  if (form) {
    form.onsubmit = async (e) => {
      e.preventDefault();
      try {
        const projId = document.getElementById('modal-pay-project').value;
        const amount = parseFloat(document.getElementById('modal-pay-amount').value) || 0;
        const date = document.getElementById('modal-pay-date').value;
        const imputationDate = document.getElementById('modal-pay-imputation-date')?.value || date;
        const invoiceFile = document.getElementById('modal-pay-invoice-file').value;
        const notes = document.getElementById('modal-pay-notes').value;

        // Obtener client_id del proyecto para mantener integridad relacional
        let clientId = null;
        try {
          const projs = await DataService.getProjects();
          const found = projs.find(p => String(p.id) === String(projId));
          if (found) clientId = found.client_id;
        } catch (_) {}

        await DataService.createPayment({
          project_id: Number(projId),
          client_id: clientId,
          payment_date: date,
          imputation_date: imputationDate,
          amount_paid: amount,
          amount_billed: amount,
          invoice_file: invoiceFile,
          notes
        });

        showToast('Cobro registrado exitosamente');
        modal.classList.add('hidden');
        document.body.classList.remove('overflow-hidden');
        window.dispatchEvent(new HashChangeEvent('hashchange'));
      } catch (err) {
        showToast(`Error: ${err.message}`, 'error');
      }
    };
  }

  // --- Manejador de Edición de Cobro ---
  document.querySelectorAll('.btn-edit-payment').forEach(btn => {
    btn.onclick = () => {
      try {
        const payData = JSON.parse(btn.dataset.payment);
        document.getElementById('edit-pay-id').value = payData.id;
        const projSelect = document.getElementById('edit-pay-project');
        if (projSelect) projSelect.value = payData.project_id || '';
        document.getElementById('edit-pay-date').value = payData.payment_date || '';
        document.getElementById('edit-pay-imputation-date').value = payData.imputation_date || payData.payment_date || '';
        document.getElementById('edit-pay-amount').value = payData.amount_paid || '';
        document.getElementById('edit-pay-invoice-file').value = payData.invoice_file || '';
        document.getElementById('edit-pay-notes').value = payData.notes || '';

        if (modalEdit) {
          modalEdit.classList.remove('hidden');
          document.body.classList.add('overflow-hidden');
        }
      } catch (err) {
        showToast(`Error al abrir editor: ${err.message}`, 'error');
      }
    };
  });

  const formEdit = document.getElementById('form-edit-payment');
  if (formEdit) {
    formEdit.onsubmit = async (e) => {
      e.preventDefault();
      try {
        const id = document.getElementById('edit-pay-id').value;
        const projId = document.getElementById('edit-pay-project').value;
        const amount = parseFloat(document.getElementById('edit-pay-amount').value) || 0;
        const date = document.getElementById('edit-pay-date').value;
        const imputationDate = document.getElementById('edit-pay-imputation-date')?.value || date;
        const invoiceFile = document.getElementById('edit-pay-invoice-file').value;
        const notes = document.getElementById('edit-pay-notes').value;

        let clientId = null;
        try {
          const projs = await DataService.getProjects();
          const found = projs.find(p => String(p.id) === String(projId));
          if (found) clientId = found.client_id;
        } catch (_) {}

        await DataService.updatePayment(id, {
          project_id: Number(projId),
          client_id: clientId,
          payment_date: date,
          imputation_date: imputationDate,
          amount_paid: amount,
          amount_billed: amount,
          invoice_file: invoiceFile,
          notes
        });

        showToast('Cobro actualizado exitosamente');
        closeEditModal();
        window.dispatchEvent(new HashChangeEvent('hashchange'));
      } catch (err) {
        showToast(`Error al actualizar cobro: ${err.message}`, 'error');
      }
    };
  }

  // --- Manejador de Eliminación de Cobro ---
  document.querySelectorAll('.btn-delete-payment').forEach(btn => {
    btn.onclick = async () => {
      const id = btn.dataset.paymentId;
      const amount = btn.dataset.paymentAmount;
      if (confirm(`¿Estás seguro de que deseas eliminar este cobro de $${Number(amount || 0).toLocaleString('es-AR')}? Esta acción no se puede deshacer.`)) {
        try {
          await DataService.deletePayment(id);
          showToast('Cobro eliminado correctamente');
          window.dispatchEvent(new HashChangeEvent('hashchange'));
        } catch (err) {
          showToast(`Error al eliminar cobro: ${err.message}`, 'error');
        }
      }
    };
  });

  // --- Filtros de Cliente y Rango de Fechas ---
  const filterClientSelect = document.getElementById('filter-finance-client');
  if (filterClientSelect) {
    filterClientSelect.onchange = (e) => {
      selectedClientId = e.target.value;
      window.dispatchEvent(new HashChangeEvent('hashchange'));
    };
  }

  const filterDateFromInput = document.getElementById('filter-finance-date-from');
  if (filterDateFromInput) {
    filterDateFromInput.onchange = (e) => {
      filterDateFrom = e.target.value;
      window.dispatchEvent(new HashChangeEvent('hashchange'));
    };
  }

  const filterDateToInput = document.getElementById('filter-finance-date-to');
  if (filterDateToInput) {
    filterDateToInput.onchange = (e) => {
      filterDateTo = e.target.value;
      window.dispatchEvent(new HashChangeEvent('hashchange'));
    };
  }

  const btnClearFilters = document.getElementById('btn-clear-finance-filters');
  if (btnClearFilters) {
    btnClearFilters.onclick = () => {
      selectedClientId = 'all';
      filterDateFrom = '';
      filterDateTo = '';
      window.dispatchEvent(new HashChangeEvent('hashchange'));
    };
  }

  // Presets de Fechas
  document.querySelectorAll('.btn-finance-preset').forEach(btn => {
    btn.onclick = () => {
      const preset = btn.dataset.preset;
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth(); // 0-indexed

      if (preset === 'all') {
        filterDateFrom = '';
        filterDateTo = '';
      } else if (preset === 'this-month') {
        const lastDay = new Date(currentYear, currentMonth + 1, 0).getDate();
        filterDateFrom = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-01`;
        filterDateTo = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      } else if (preset === 'last-month') {
        const prevYear = currentMonth === 0 ? currentYear - 1 : currentYear;
        const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
        const lastDay = new Date(prevYear, prevMonth + 1, 0).getDate();
        filterDateFrom = `${prevYear}-${String(prevMonth + 1).padStart(2, '0')}-01`;
        filterDateTo = `${prevYear}-${String(prevMonth + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      } else if (preset === 'this-year') {
        filterDateFrom = `${currentYear}-01-01`;
        filterDateTo = `${currentYear}-12-31`;
      }
      window.dispatchEvent(new HashChangeEvent('hashchange'));
    };
  });

  // Search and collapse for Projects Table
  const searchProjectsInput = document.getElementById('input-search-projects-fin');
  const btnClearProjects = document.getElementById('btn-clear-projects-fin-search');
  let searchProjTimer = null;
  if (searchProjectsInput) {
    searchProjectsInput.addEventListener('input', (e) => {
      clearTimeout(searchProjTimer);
      searchProjTimer = setTimeout(() => {
        projectSearchTerm = e.target.value;
        window.dispatchEvent(new HashChangeEvent('hashchange'));
        setTimeout(() => {
          const input = document.getElementById('input-search-projects-fin');
          if (input) {
            input.focus();
            input.setSelectionRange(input.value.length, input.value.length);
          }
        }, 50);
      }, 180);
    });
  }
  if (btnClearProjects) {
    btnClearProjects.onclick = () => {
      projectSearchTerm = '';
      window.dispatchEvent(new HashChangeEvent('hashchange'));
    };
  }

  const btnToggleProjects = document.getElementById('btn-toggle-projects-collapse');
  if (btnToggleProjects) {
    btnToggleProjects.onclick = () => {
      projectsCollapsed = !projectsCollapsed;
      const container = document.getElementById('container-projects-fin-table');
      if (container) {
        container.classList.toggle('hidden', projectsCollapsed);
      }
      const iconSpan = btnToggleProjects.querySelector('.collapse-icon');
      if (iconSpan) {
        iconSpan.innerHTML = getIcon(projectsCollapsed ? 'chevron-right' : 'chevron-down', 'w-3.5 h-3.5');
      }
      const labelSpan = btnToggleProjects.querySelector('.collapse-label');
      if (labelSpan) {
        labelSpan.textContent = projectsCollapsed ? 'Expandir' : 'Plegar';
      }
      btnToggleProjects.title = projectsCollapsed ? 'Expandir tabla' : 'Plegar tabla';
    };
  }

  // Search and collapse for Payments
  const searchPaymentsInput = document.getElementById('input-search-payments');
  const btnClearPayments = document.getElementById('btn-clear-payments-search');
  let searchPayTimer = null;
  if (searchPaymentsInput) {
    searchPaymentsInput.addEventListener('input', (e) => {
      clearTimeout(searchPayTimer);
      searchPayTimer = setTimeout(() => {
        paymentSearchTerm = e.target.value;
        window.dispatchEvent(new HashChangeEvent('hashchange'));
        setTimeout(() => {
          const input = document.getElementById('input-search-payments');
          if (input) {
            input.focus();
            input.setSelectionRange(input.value.length, input.value.length);
          }
        }, 50);
      }, 180);
    });
  }
  if (btnClearPayments) {
    btnClearPayments.onclick = () => {
      paymentSearchTerm = '';
      window.dispatchEvent(new HashChangeEvent('hashchange'));
    };
  }

  const btnTogglePayments = document.getElementById('btn-toggle-payments-collapse');
  if (btnTogglePayments) {
    btnTogglePayments.onclick = () => {
      paymentsCollapsed = !paymentsCollapsed;
      const container = document.getElementById('container-payments-table');
      if (container) {
        container.classList.toggle('hidden', paymentsCollapsed);
      }
      const iconSpan = btnTogglePayments.querySelector('.collapse-icon');
      if (iconSpan) {
        iconSpan.innerHTML = getIcon(paymentsCollapsed ? 'chevron-right' : 'chevron-down', 'w-3.5 h-3.5');
      }
      const labelSpan = btnTogglePayments.querySelector('.collapse-label');
      if (labelSpan) {
        labelSpan.textContent = paymentsCollapsed ? 'Expandir' : 'Plegar';
      }
      btnTogglePayments.title = paymentsCollapsed ? 'Expandir tabla' : 'Plegar tabla';
    };
  }

  // Ordenamiento de columnas por clic en cabeceras de tablas
  document.querySelectorAll('th[data-sort-table]').forEach(th => {
    th.onclick = () => {
      const table = th.dataset.sortTable;
      const field = th.dataset.sortField;
      if (table === 'projects') {
        if (projectsSortField === field) {
          projectsSortAsc = !projectsSortAsc;
        } else {
          projectsSortField = field;
          projectsSortAsc = !['devengado', 'cobrado', 'facturado', 'pendienteCobro'].includes(field);
        }
      } else if (table === 'payments') {
        if (paymentsSortField === field) {
          paymentsSortAsc = !paymentsSortAsc;
        } else {
          paymentsSortField = field;
          paymentsSortAsc = !['payment_date', 'imputation_date', 'amount_paid'].includes(field);
        }
      }
      window.dispatchEvent(new HashChangeEvent('hashchange'));
    };
  });
}
